import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProjectDetail.css';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCalendarAlt, FaTag, FaCheckCircle, FaTools } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';

const ProjectDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const renderDescription = (text) => {
        if (!text) return null;

        // Regex to identify URLs
        const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

        // Split text by URLs and map through parts
        const parts = text.split(urlRegex);

        return parts.map((part, index) => {
            if (part && part.match(urlRegex)) {
                const href = part.startsWith('http') ? part : `https://${part}`;
                return (
                    <a
                        key={index}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="description-link"
                    >
                        {part}
                    </a>
                );
            }
            return part;
        });
    };

    useEffect(() => {
        const fetchProject = async () => {
            setIsLoading(true);
            try {
                const { ok, data: result } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/projects/${id}`);
                if (ok && result.data) {
                    setProject(result.data);
                } else {
                    console.error('Project not found');
                }
            } catch (error) {
                console.error('Error fetching project detail:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProject();
        window.scrollTo(0, 0);
    }, [id]);

    if (isLoading) {
        return (
            <div className="project-detail-container">
                <Navbar />
                <div className="detail-loading" style={{ textAlign: 'center', padding: '200px 0' }}>
                    <div className="loader"></div>
                    <p style={{ marginTop: '20px', color: '#94a3b8' }}>Loading project details...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (!project) {
        return (
            <div className="project-detail-container">
                <Navbar />
                <div className="detail-error" style={{ textAlign: 'center', padding: '200px 0' }}>
                    <h2>Project Not Found</h2>
                    <button className="primary-btn" onClick={() => navigate('/portfolio')}>Back to Portfolio</button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="project-detail-container">
            <Navbar />

            <section className="detail-hero" style={{ backgroundColor: project.theme_color || '#0E1629' }}>
                <div className="detail-hero-content">
                    <motion.button
                        className="back-btn"
                        onClick={() => navigate('/portfolio')}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <FaArrowLeft /> Back to Portfolio
                    </motion.button>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <span className="project-cat">{project.service?.title || 'Innovation'}</span>
                        <h1>{project.title}</h1>
                    </motion.div>
                </div>
                <div className="hero-shape"></div>
            </section>

            <div className="detail-content-wrapper">
                <div className="detail-main-grid">
                    <motion.div
                        className="detail-main-content"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <div className="detail-image-main">
                            <img src={project.image_url || project.image} alt={project.title} />
                        </div>

                        <div className="detail-text-block">
                            <h2>Project Overview</h2>
                            <p>{renderDescription(project.description)}</p>
                        </div>

                        {project.features && project.features.length > 0 && (
                            <div className="detail-features">
                                <h2>Core Features</h2>
                                <div className="features-grid">
                                    {project.features.map((feature, i) => (
                                        <div key={i} className="feature-item">
                                            <FaCheckCircle /> {feature}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>

                    <motion.aside
                        className="detail-sidebar"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                    >
                        <div className="sidebar-card">
                            <h3>Project Info</h3>
                            <div className="info-item">
                                <FaCalendarAlt />
                                <div>
                                    <label>Timeline</label>
                                    <span>{project.date}</span>
                                </div>
                            </div>
                            <div className="info-item">
                                <FaTag />
                                <div>
                                    <label>Client</label>
                                    <span>{project.client}</span>
                                </div>
                            </div>
                            <div className="info-item">
                                <FaTools />
                                <div>
                                    <label>Industry</label>
                                    <div className="sidebar-tags">
                                        <span>{project.service?.title || 'General'}</span>
                                    </div>
                                </div>
                            </div>


                        </div>
                    </motion.aside>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ProjectDetail;
